const changeAdStatusTool = () => {
  return {
    name: "change_ad_status",
    description: "Enable or pause a list of media plans given their advertisement ids.",
    parameters: {
      type: "object",
      properties: {
        state: {
          type: "string",
          enum: ["paused", "enabled"],
          description: "The state to set the ads to",
        },
        advertisement_ids: {
          type: "array",
          items: {
            type: "string",
            description: "The ID of the media plan to update",
          },
        },
      },
      required: ["advertisement_ids", "state"],
    },
  };
};
const setAdvertisementDaypartingTool = () => {
  return {
    name: "set_advertisement_dayparting",
    description:
      "Execute dayparting changes using the payload already received from advertisement_bulk_actions. Call this IMMEDIATELY after advertisement_bulk_actions returns mode='bulk_update_dayparting'. Extract all three parameters from the payload you just received: (1) advertisement_ids from payload.advertisements, (2) day_parting_method from payload.day_parting_method, (3) day_parting_multipliers from payload.day_parting_multipliers - convert the object to an array of {key, value} objects. Do NOT make additional MCP calls - use the data already in memory.",
    strict: true,
    parameters: {
      type: "object",
      required: ["advertisement_ids", "day_parting_method", "day_parting_multipliers"],
      properties: {
        advertisement_ids: {
          type: "array",
          items: {
            type: "string",
            description: "Advertisement ID as string",
          },
          description: "Extract from payload.advertisements (convert integers to strings)",
        },
        day_parting_method: {
          type: "integer",
          description: "Extract from payload.day_parting_method (always 1 for dayparting)",
        },
        day_parting_multipliers: {
          type: "array",
          description:
            "REQUIRED. Convert payload.day_parting_multipliers object to an array of multiplier entries. Each entry must have 'key' (hour-day format like '16-3') and 'value' (multiplier as string like '3').",
          items: {
            type: "object",
            required: ["key", "value"],
            properties: {
              key: {
                type: "string",
                description: "Hour-day key in format '{hour}-{day}', e.g., '16-3' for 4pm Thursday",
              },
              value: {
                type: "string",
                description: "Multiplier value as string, e.g., '3' or '5'",
              },
            },
            additionalProperties: false,
          },
        },
      },
      additionalProperties: false,
    },
  };
};

const setAdvertisementBulkUpdateTool = () => {
  return {
    name: "set_advertisement_bulk_update",
    description:
      "Apply one or more bulk property updates to a list of media plans using the payload you just received from advertisement_bulk_actions. Do not perform additional MCP calls; reuse the data already returned.",
    strict: true,
    parameters: {
      type: "object",
      required: ["advertisement_ids", "updates"],
      properties: {
        advertisement_ids: {
          type: "array",
          description: "List of advertisement IDs to update",
          items: {
            type: "string",
            description: "The ID of the media plan to update",
          },
        },
        updates: {
          type: "array",
          description:
            "Array of bulk update operations. Forward the exact updates array received from advertisement_bulk_actions without modification.",
          items: {
            type: "object",
            required: ["action", "field", "value"],
            properties: {
              action: {
                type: "string",
                description: "Type of update to apply (matches advertisement_bulk_actions response).",
                enum: ["set", "add", "remove", "percent", "fixed"],
              },
              field: {
                type: "string",
                description: "Field on the media plan that should be updated.",
                enum: [
                  "tags",
                  "daily_budget",
                  "target_acos",
                  "max_acos",
                  "target_roas",
                  "max_roas",
                  "bidding_rules",
                  "budget_rules",
                  "bid_algo",
                  "category",
                  "bid_adjustments_by_placement",
                  "shopper_cohort_bidding",
                  "bid_adjustments_by_shopper_segment",
                  "cancel_scheduled_actions",
                  "week_parting_method",
                  "week_parting_multipliers",
                  "sales_volume_control_slider",
                  "non_trellis_enrolled_ad",
                  "non_trellis_enrolled_ad_accept_bids",
                ],
              },
              value: {
                description:
                  "Payload for the chosen action/field. Pass the value exactly as received, JSON-stringifying objects/arrays if needed.",
                type: "string",
              },
            },
            additionalProperties: false,
          },
          additionalProperties: false,
        },
      },
      additionalProperties: false,
    },
  };
};

const scheduleBudgetTool = () => {
  return {
    name: "schedule_budget_tool",
    description:
      "Schedule a temporary budget change for the supplied media plans using data already returned from advertisement_bulk_actions.",
    strict: true,
    parameters: {
      type: "object",
      required: ["channel", "start_date", "end_date", "fields", "gvadvertisements_set"],
      properties: {
        channel: {
          type: "string",
          description: "Marketplace channel code (e.g., AMZ).",
        },
        start_date: {
          type: "string",
          description: "ISO timestamp when the scheduled action starts.",
        },
        end_date: {
          type: "string",
          description: "ISO timestamp when the scheduled action ends.",
        },
        gvadvertisements_set: {
          type: "array",
          description: "List of advertisement IDs the schedule applies to.",
          items: {
            type: ["number", "string"],
          },
        },
        fields: {
          type: "array",
          description: "Field-level changes to apply during the schedule.",
          items: {
            type: "object",
            required: ["field_name", "change_type", "change_value", "rollback"],
            properties: {
              field_name: {
                type: "string",
                description: "Field being modified (e.g., daily_budget).",
              },
              change_type: {
                type: "string",
                enum: ["percentage_change", "flat_change"],
                description: "Type of change to apply.",
              },
              change_value: {
                type: "number",
                description: "Amount to apply for the change type.",
              },
              rollback: {
                type: "boolean",
                description: "Whether to automatically rollback changes after end_date.",
              },
            },
            additionalProperties: false,
          },
        },
      },
      additionalProperties: false,
    },
  };
};

export { changeAdStatusTool, setAdvertisementDaypartingTool, setAdvertisementBulkUpdateTool, scheduleBudgetTool };
